/**
 * Sort options for Norce `ListProducts2` (category + manufacturer listings).
 *
 * Norce sorts by `PopularityRank`, `Name`, `Price` or `SortOrder`, each
 * optionally suffixed with ` Asc`/` Desc`. When `sort` is omitted Norce falls
 * back to `Name`, so listings always send a value explicitly.
 * https://docs.norce.io/developer-portal/app-development/paging-and-sorting-lists
 */
// The labels are rendered as `t(labelKey)` in the listing components, which
// `npm run check:i18n` cannot trace, so the keys are spelled out literally here
// to keep them off the unused-key list:
// t("Recommended"), t("Name A-Z"), t("Name Z-A"), t("Price ascending"),
// t("Price descending")
const PRODUCT_SORT_OPTIONS = [
	{ labelKey: "Recommended", value: "SortOrder Asc" },
	{ labelKey: "Name A-Z", value: "Name Asc" },
	{ labelKey: "Name Z-A", value: "Name Desc" },
	{ labelKey: "Price ascending", value: "Price Asc" },
	{ labelKey: "Price descending", value: "Price Desc" }
] as const;

type ProductSort = (typeof PRODUCT_SORT_OPTIONS)[number]["value"];

/**
 * Catalog default: the manual order maintained in Norce admin (requires
 * activation in Norce settings). Norce ranks ascending from 1, so `Asc` is the
 * order the merchandiser actually configured. The direction is spelled out
 * because the API reference documents the format as `[Property] [Asc/Desc]`
 * and never states which way a bare property name sorts.
 */
const DEFAULT_PRODUCT_SORT: ProductSort = "SortOrder Asc";

const isProductSort = (value: unknown): value is ProductSort =>
	PRODUCT_SORT_OPTIONS.some((option) => option.value === value);

/**
 * `?sort` is user input that is forwarded to Norce as-is, so anything outside
 * the supported set — including the legacy `"SortOrder Desc"`/`"SortOrder"`
 * values still sitting in bookmarks and indexed URLs — collapses to the
 * default rather than reaching the API or leaving the sort dropdown without a
 * matching label.
 */
const normalizeProductSort = (value: unknown): ProductSort =>
	isProductSort(value) ? value : DEFAULT_PRODUCT_SORT;

export {
	PRODUCT_SORT_OPTIONS,
	DEFAULT_PRODUCT_SORT,
	isProductSort,
	normalizeProductSort
};
export type { ProductSort };
